import { Box, Flex, Menu, Portal, Text } from '@chakra-ui/react';
import { HiEllipsisVertical } from 'react-icons/hi2';

export interface RowAction {
  label: string;
  onClick: () => void;
  permission?: boolean;
  variant?: 'default' | 'danger';
  e2eTitle?: string;
  icon?: React.ReactNode;
  isPrimary?: boolean;
  disabled?: boolean;
}

export interface RowActionsMenuProps {
  actions: RowAction[];
}

/**
 * @useFor the per-row action dropdown in the last column of a
 *   `<DataTable>`: Edit / Delete / Duplicate / etc.
 *
 *   Examples in-tree: People row → Edit / Deactivate; Onboarding job
 *   row → Re-run / Cancel; Roles list → Edit / Delete.
 *
 *   Action visibility is permission-gated via `action.permission`
 *   (set false to hide); if every action filters out, the menu
 *   renders nothing rather than showing an empty trigger.
 *
 *   For one-off "primary action" buttons on a row use `<Button>`
 *   directly. For bulk actions on the whole table use a header
 *   button in `<TableToolbar>`, not a row menu.
 */
export function RowActionsMenu({ actions }: RowActionsMenuProps) {
  const visibleActions = actions.filter((action) => action.permission !== false);

  if (visibleActions.length === 0) {
    return (
      <Flex justifyContent="center" color="text.tertiary">
        <HiEllipsisVertical size={22} />
      </Flex>
    );
  }

  const primaryActions = visibleActions.filter((action) => action.isPrimary);
  const secondaryActions = visibleActions.filter((action) => !action.isPrimary);

  const renderMenuItem = (action: RowAction, index: number) => (
    <Menu.Item
      key={index}
      value={action.label}
      data-speak={action.label}
      data-e2e={action.e2eTitle}
      textTransform="capitalize"
      fontSize="14px"
      fontWeight={600}
      bg="surface.primary"
      color={action.variant === 'danger' ? 'red.500' : 'text.primary'}
      border="none"
      borderRadius="none"
      disabled={action.disabled}
      onClick={(e: React.MouseEvent) => {
        e.stopPropagation();
        action.onClick();
      }}
      _hover={{
        bg: 'surface.secondary',
        cursor: 'pointer',
      }}
      _focus={{
        bg: 'surface.secondary',
      }}
      _focusVisible={{
        outline: '2px solid',
        outlineColor: 'border.action',
        outlineOffset: '-2px',
      }}
      py="10px"
      px="16px"
    >
      <Flex alignItems="center" gap={2}>
        {action.icon && (
          <Box fontSize="16px" display="flex" alignItems="center">
            {action.icon}
          </Box>
        )}
        <Text>{action.label}</Text>
      </Flex>
    </Menu.Item>
  );

  return (
    <Menu.Root positioning={{ sameWidth: false }}>
      <Menu.Trigger asChild>
        <Flex
          justifyContent="center"
          cursor="pointer"
          color="text.action"
          onClick={(e) => e.stopPropagation()}
        >
          <HiEllipsisVertical size={22} />
        </Flex>
      </Menu.Trigger>
      <Portal>
        <Menu.Positioner>
          <Menu.Content
            minWidth="150px"
            padding="0px"
            bg="surface.primary"
            border="1px solid"
            borderColor="border.secondary"
            borderRadius="md"
            overflow="hidden"
          >
            {primaryActions.map((action, index) => renderMenuItem(action, index))}

            {primaryActions.length > 0 && secondaryActions.length > 0 && (
              <Box
                borderTop="1px solid"
                borderColor="border.secondary"
                my="4px"
              />
            )}

            {secondaryActions.map((action, index) => renderMenuItem(action, index))}
          </Menu.Content>
        </Menu.Positioner>
      </Portal>
    </Menu.Root>
  );
}
